import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useRouterStore } from "../../../stores";
import {
  DASHBOARD_ADMINISTRATOR_CODE,
  DASHBOARD_CLIENT_ADMINISTRATOR_CODE,
  URL_BACKEND,
} from "../../../constants";
import {
  notificationMongoAllUnread,
  notificationMongoLastTen,
  notificationMongoUpdate,
} from "../../../apis";
import { NotificationMongoSchemaI } from "../../../interfaces";
import "../../css/notification-bell.css";

interface Notification {
  message: string;
  reportId: number;
  timestamp: string;
  link: string;
}

export const NotificationBell: React.FC = () => {
  const navigate = useNavigate();
  const clientId = useRouterStore((state) => state.clientId);
  const typeDashboardCode = useRouterStore((state) => state.typeDashboardCode);
  const [notifications, setNotifications] = useState<NotificationMongoSchemaI[]>([]);
  const [notificationUnread, setNotificationUnread] = useState<Notification[]>([]);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Efecto para cerrar el dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Cargar notificaciones al montar el componente
  useEffect(() => {
    fetchNotifications();
  }, []);

  // Configurar EventSource para notificaciones en tiempo real
  useEffect(() => {
    let eventSource: EventSource | null = null;
    let retryTimeout: NodeJS.Timeout | null = null;

    const connectSSE = (): void => {
      if (eventSource) {
        eventSource.close();
      }

      let sseUrl: string = "";
      if (typeDashboardCode === DASHBOARD_CLIENT_ADMINISTRATOR_CODE) {
        sseUrl = `${URL_BACKEND}/api/notifications-sse?id=${clientId}`;
      } else if (typeDashboardCode === DASHBOARD_ADMINISTRATOR_CODE) {
        sseUrl = `${URL_BACKEND}/api/notifications-sse/admin-secure`;
      } else {
        console.log("El usuario no es administrador, no se conecta a SSE.");
        return;
      }

      eventSource = new EventSource(sseUrl);

      eventSource.onmessage = (event: MessageEvent): void => {
        const notification: Notification = JSON.parse(event.data);
        // Añadir animación a las nuevas notificaciones
        setNotificationUnread((prev) => [...prev, notification]);
        
        // Opcional: Reproducir un sonido de notificación
        playNotificationSound();
      };

      eventSource.onerror = (): void => {
        console.error("Error en la conexión SSE, reintentando en 5 segundos...");
        eventSource?.close();

        retryTimeout = setTimeout(() => {
          connectSSE();
        }, 5000);
      };

      eventSource.onopen = (): void => {
        console.log("Conexión SSE establecida correctamente.");
        if (retryTimeout) clearTimeout(retryTimeout);
      };
    };

    connectSSE();

    return () => {
      if (eventSource) eventSource.close();
      if (retryTimeout) clearTimeout(retryTimeout);
    };
  }, [clientId, typeDashboardCode]);

  // Función para reproducir un sonido de notificación
  const playNotificationSound = (): void => {
    // Puedes implementar un sonido personalizado aquí
    // const audio = new Audio('/notification-sound.mp3');
    // audio.play().catch(e => console.log('Error playing sound:', e));
  };

  // Función para formatear fechas
  const formatDate = (date: string | Date): string => {
    const options: Intl.DateTimeFormatOptions = {
      weekday: "short",
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    };

    const elDate = new Date(date);
    return new Intl.DateTimeFormat("es-ES", options).format(elDate);
  };

  // Obtener notificaciones desde la API
  const fetchNotifications = async (): Promise<void> => {
    try {
      const [unreadRes, lastTenRes] = await Promise.all([
        notificationMongoAllUnread(),
        notificationMongoLastTen(),
      ]);

      if (unreadRes.data) setNotificationUnread(unreadRes.data as any);
      if (lastTenRes.data) setNotifications(lastTenRes.data as any);
    } catch (err) {
      console.error("Error al obtener notificaciones:", err instanceof Error ? err.message : String(err));
      // Implementar manejo de errores personalizado aquí
    }
  };

  // Actualizar notificaciones al cerrar el dropdown
  const handleDropdownToggle = (): void => {
    if (!isOpen) {
      // Al abrir, cargar las últimas notificaciones
      handleGetLastTen();
    } else {
      // Al cerrar, marcar como leídas
      updateNotifications();
      setNotificationUnread([]);
    }
    setIsOpen(!isOpen);
  };

  // Obtener las últimas 10 notificaciones
  const handleGetLastTen = async (): Promise<void> => {
    try {
      const { data: dataNotifications } = await notificationMongoLastTen();
      if (dataNotifications) {
        setNotifications(dataNotifications as any);
      }
    } catch (err) {
      console.error("Error al obtener notificaciones:", err instanceof Error ? err.message : String(err));
    }
  };

  // Marcar notificaciones como leídas
  const updateNotifications = async (): Promise<void> => {
    try {
      await notificationMongoUpdate();
    } catch (err) {
      console.error("Error al actualizar notificaciones:", err instanceof Error ? err.message : String(err));
    }
  };

  // Navegar a la página relacionada con la notificación
  const handleNotificationClick = (link: string): void => {
    navigate(link);
    setIsOpen(false);
  };

  return (
    <div className="notification-container" ref={dropdownRef}>
      <button 
        className="notification-bell-button" 
        onClick={handleDropdownToggle}
        aria-label="Notificaciones"
      >
        <i className="notification-bell-icon"></i>
        {notificationUnread.length > 0 && (
          <span className="notification-badge">{notificationUnread.length}</span>
        )}
      </button>

      {isOpen && (
        <div className="notification-dropdown">
          <div className="notification-header">
            <h3>Notificaciones</h3>
            {notifications.length > 0 && (
              <button 
                className="mark-read-button"
                onClick={() => {
                  updateNotifications();
                  setNotificationUnread([]);
                }}
              >
                Marcar todas como leídas
              </button>
            )}
          </div>
          
          <div className="notification-list">
            {notifications.length === 0 ? (
              <div className="no-notifications">
                No hay notificaciones disponibles
              </div>
            ) : (
              notifications.map((notification, index) => (
                <div 
                  key={`notification-${index}`}
                  className={`notification-item ${notification.status === "unread" ? "unread" : ""}`}
                  style={{
                    backgroundColor: notification.status === "unread" 
                      ? (notification.bg || "#DC143C") 
                      : "white"
                  }}
                  onClick={() => handleNotificationClick(notification.link)}
                >
                  <div className="notification-icon">
                    <i className="alert-icon"></i>
                  </div>
                  <div className="notification-content">
                    <div 
                      className="notification-message"
                      style={{
                        color: notification.status === "unread" 
                          ? "white" 
                          : (notification.bg === "red" ? "red" : "#006400")
                      }}
                    >
                      {notification.message}
                    </div>
                    <div 
                      className="notification-user"
                      style={{
                        color: notification.status === "unread" ? "white" : "#0a0521"
                      }}
                    >
                      {notification.userCreation}
                    </div>
                    <div 
                      className="notification-time"
                      style={{
                        color: notification.status === "unread" ? "white" : "#1c0f57"
                      }}
                    >
                      {formatDate(notification.createdAt)}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;